import { ChevronDown, Plus, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Column } from '@tanstack/react-table'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'

import { useTableContext } from './TableContext'
import { useLogisticCompany } from '../hooks/useLogisticCompany'
import NewCompanyModal from '../components/NewCompanyModal'
import { Modal } from '@/components/new'
import { Company } from '@/interfaces/company'

export const HeaderActions = ({
  columns,
}: {
  columns: Column<Company, unknown>[]
}) => {
  // Use the context
  const {
    currentPage,
    pageSize,
    companyData,
    isLoading,
    refetch,
    setPageSize,
  } = useTableContext()

  const [isMobileView, setIsMobileView] = useState(false)

  const {
    onNewCompany,
    showNewCompany,
    editExpedient: _editExpedient, // DEBUG: _editExpedient available
    onClose,
  } = useLogisticCompany()

  // Reference unused variable to satisfy linting
  void _editExpedient

  // Check for mobile view on mount and window resize
  useEffect(() => {
    const checkViewport = () => {
      setIsMobileView(window.innerWidth < 768)
    }

    checkViewport()
    window.addEventListener('resize', checkViewport)

    return () => {
      window.removeEventListener('resize', checkViewport)
    }
  }, [])

  // For mobile: stacked layout
  if (isMobileView) {
    return (
      <>
        {' '}
        <div className="flex-col gap-4 p-4">
          <div className="mb-4 flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                aria-label="Refrescar tabla"
                className="h-8 w-8"
                disabled={isLoading}
                onClick={refetch}
                size="icon"
                variant="ghost"
              >
                {isLoading ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    aria-label="Mostrar columnas"
                    className="h-8 gap-1"
                    size="sm"
                    variant="outline"
                  >
                    <span>Columnas</span>
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>Mostrar columnas</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {columns
                    .filter((column) => column.getCanHide())
                    .map((column) => {
                      return (
                        <DropdownMenuCheckboxItem
                          checked={column.getIsVisible()}
                          className="cursor-pointer"
                          key={column.id}
                          onCheckedChange={(value) =>
                            column.toggleVisibility(!!value)
                          }
                        >
                          {typeof column.columnDef.header === 'string'
                            ? column.columnDef.header
                            : column.id}
                        </DropdownMenuCheckboxItem>
                      )
                    })}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    aria-label="Mostrar columnas"
                    className="h-8 gap-1"
                    size="sm"
                    variant="outline"
                  >
                    <span>Resultados por página: {pageSize}</span>
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>Resultados por página</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="hover:cursor-pointer hover:bg-muted-foreground/10"
                    onClick={() => setPageSize(20)}
                  >
                    <span>20</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="hover:cursor-pointer hover:bg-muted-foreground/10"
                    onClick={() => setPageSize(50)}
                  >
                    <span>50</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="hover:cursor-pointer hover:bg-muted-foreground/10"
                    onClick={() => setPageSize(100)}
                  >
                    <span>100</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="hover:cursor-pointer hover:bg-muted-foreground/10"
                    onClick={() => setPageSize(500)}
                  >
                    <span>500</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="bg-muted-foregrohover:und hover:cursor/10-pointer"
                    onClick={() => setPageSize(1000)}
                  >
                    <span>1000</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* <DropdownMenu>
           <DropdownMenuTrigger asChild>
             <Button
               variant="outline"
               size="sm"
               className="h-8 gap-1"
               aria-label="Exportar datos"
             >
               <span>Exportar</span>
               <ChevronDown className="h-3.5 w-3.5" />
             </Button>
           </DropdownMenuTrigger>
           <DropdownMenuContent align="end" className="w-48">
             <DropdownMenuLabel>Exportar</DropdownMenuLabel>
             <DropdownMenuSeparator />
             <DropdownMenuItem>
               <BsFilePdf className="mr-2 h-4 w-4" />
               <span>Exportar a PDF</span>
             </DropdownMenuItem>
             <DropdownMenuItem>
               <FileSpreadsheet className="mr-2 h-4 w-4" />
               <span>Exportar a Excel</span>
             </DropdownMenuItem>
           </DropdownMenuContent>
         </DropdownMenu> */}
            </div>
          </div>

          <div className="flex w-full flex-col gap-2">
            <Button
              className="h-8 w-full gap-1"
              onClick={onNewCompany}
              size="sm"
              variant="outline"
            >
              <Plus className="h-4 w-4" />
              <span>Nueva compañía</span>
            </Button>
            <Modal isOpen={showNewCompany} onClose={onClose} size={'xs'}>
              <NewCompanyModal close={onClose} />
            </Modal>
          </div>
        </div>
        {/*(showNewExpedient || !!editExpedient) && (
          <ExpedientWizard
            showState={showNewExpedient}
            onClose={onCloseWizard}
            editExpedient={editExpedient}
          />
        )*/}
      </>
    )
  }

  // For desktop: layout with creation buttons on left, pagination in middle, and utility buttons on right
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 p-4">
        {/* Left - Invoice creation buttons */}
        <div className="order-1 flex items-center gap-2">
          <Button
            className="h-8 gap-1"
            onClick={onNewCompany}
            size="sm"
            variant="outline"
          >
            <Plus className="h-4 w-4" />
            <span>Nueva compañía</span>
          </Button>
          <Modal isOpen={showNewCompany} onClose={onClose} size={'xs'}>
            <NewCompanyModal close={onClose} />
          </Modal>
        </div>

        {/* Middle - Pagination info */}
        <div className="order-3 flex flex-1 items-center justify-center gap-2 xl:order-2">
          <Button
            aria-label="Refrescar tabla"
            className="h-8 w-8"
            disabled={isLoading}
            onClick={refetch}
            size="icon"
            variant="ghost"
          >
            {isLoading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
          <span className="min-h-[20px] min-w-[300px] text-sm text-muted-foreground">
            {isLoading ? (
              <Skeleton className="h-4 w-[300px]" />
            ) : companyData?.totalItems ? (
              <>
                Mostrando {(currentPage - 1) * pageSize + 1} al{' '}
                {Math.min(currentPage * pageSize, companyData.totalItems)} de{' '}
                {companyData.totalItems} resultados
              </>
            ) : (
              'No hay resultados'
            )}
          </span>
        </div>

        {/* Right - Export and columns buttons */}
        <div className="order-2 flex items-center gap-2 xl:order-3">
          {/* <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1"
            aria-label="Exportar datos"
            disabled={isLoading}
          >
            <span>Exportar</span>
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel>Exportar</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem>
            <BsFilePdf className="mr-2 h-4 w-4" />
            <span>Exportar a PDF</span>
          </DropdownMenuItem>
          <DropdownMenuItem>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            <span>Exportar a Excel</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu> */}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label="Mostrar columnas"
                className="h-8 gap-1"
                size="sm"
                variant="outline"
              >
                <span>Columnas</span>
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Mostrar columnas</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {columns
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      checked={column.getIsVisible()}
                      className="cursor-pointer"
                      key={column.id}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {typeof column.columnDef.header === 'string'
                        ? column.columnDef.header
                        : column.id}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label="Mostrar columnas"
                className="h-8 gap-1"
                size="sm"
                variant="outline"
              >
                <span>Resultados por página: {pageSize}</span>
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Resultados por página</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="hover:cursor-pointer hover:bg-muted-foreground/10"
                onClick={() => setPageSize(20)}
              >
                <span>20</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="hover:cursor-pointer hover:bg-muted-foreground/10"
                onClick={() => setPageSize(50)}
              >
                <span>50</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="hover:cursor-pointer hover:bg-muted-foreground/10"
                onClick={() => setPageSize(100)}
              >
                <span>100</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="hover:cursor-pointer hover:bg-muted-foreground/10"
                onClick={() => setPageSize(500)}
              >
                <span>500</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="hover:cursor-pointer hover:bg-muted-foreground/10"
                onClick={() => setPageSize(1000)}
              >
                <span>1000</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {/*(showNewExpedient || !!editExpedient) && (
        <ExpedientWizard
          showState={showNewExpedient}
          onClose={onCloseWizard}
          editExpedient={editExpedient}
        />
      )*/}
    </>
  )
}
